<script setup lang="ts">
    import { ref, onMounted, watch, reactive, provide, computed } from 'vue';
    import { RouterView, useRoute, useRouter } from 'vue-router';
    import TextMessagesTopNav from './TextMessages/Shared/TextMessagesTopNav.vue';
    import EventNavbarTopNav from './Event/EventNavbarTopNav.vue';
    import Loading from '@shared/components/Loading.vue';

    export type displayMode = 'default' | 'editor';

    const isLoading = ref<boolean>(true);
    const currentRouteName = ref<null | string>(null);
    const route = useRoute();
    const router = useRouter();
    const routeHistory = ref<string[]>([]);

    const state = reactive<{
        mode: displayMode;
    }>({
        mode: 'default',
    });

    provide('mode', state.mode);

    onMounted(async () => {
        isLoading.value = false;
        currentRouteName.value = route.name as string;

        // Initialize route history with current route
        if (route.fullPath) {
            routeHistory.value.push(route.fullPath);
        }

        watch(
            () => route?.fullPath,
            async (newPath) => {
                if (newPath && newPath !== routeHistory.value[routeHistory.value.length - 1]) {
                    routeHistory.value.push(newPath);
                    // Keep only last 10 routes to prevent memory issues
                    if (routeHistory.value.length > 10) {
                        routeHistory.value.shift();
                    }
                }
                currentRouteName.value = route.name as string;
            }
        );
    });

    const goBack = () => {
        if (routeHistory.value.length > 1) {
            // Remove current route and go to previous
            routeHistory.value.pop();
            const previousRoute = routeHistory.value[routeHistory.value.length - 1];
            router.push(previousRoute);
        } else {
            // Fallback to go back in browser history
            router.go(-1);
        }
    };

    const showRouteReturn = computed(() => {
        switch (route.name) {
            case 'FormsEditor':
                return true;
            case 'FormsNew':
                return true;
            default:
                return false;
        }
    });

    const getTransitionName = (route: any) => {
        if (
            route.path.startsWith('/BackOffice') ||
            route.path.startsWith('/Journeys') ||
            route.path.startsWith('/Account') ||
            route.path.startsWith('/Events/Editor2')
        ) {
            return undefined;
        }
        return 'route';
    };

    const getRouteKey = (path: string) => {
        const eventEditorMatch = path.match(/\/Events\/Editor2\/[0-9a-f-]+/i);
        if (eventEditorMatch) return eventEditorMatch[0];

        const issueMatch = path.match(/\/Issues\/[0-9a-f-]+\/Statistics/i);
        if (issueMatch) return path.slice(0, issueMatch.index! + issueMatch[0].length);

        const surveyStatisticsMatch = path.match(/\/Surveys\/[0-9a-f-]+\/Results/i);
        if (surveyStatisticsMatch) return path.slice(0, surveyStatisticsMatch.index! + surveyStatisticsMatch[0].length);

        const listContactMatch = path.match(/(\/Lists\/[0-9a-f-]+)\/(Contact\/|Import)/i);
        if (listContactMatch) return listContactMatch[1];

        const contactsContactMatch = path.match(/^(\/Contacts)\/Contact\//i);
        if (contactsContactMatch) return contactsContactMatch[1];

        return path;
    };
</script>

<template>
    <a href="javascript:void(0)" role="button" class="route-return" v-if="showRouteReturn" @click="goBack">
        <span class="glyphicon glyphicon-chevron-left m-xxs-r c-light"></span>
        {{ $Resources.BackButton }}
    </a>

    <TextMessagesTopNav v-if="route.path.startsWith('/TextMessages')" :currentRouteName="currentRouteName" />
    <EventNavbarTopNav :currentRouteName="currentRouteName" />

    <Suspense v-if="!isLoading">
        <router-view v-slot="{ Component, route: currentRoute }">
            <keep-alive>
                <Transition :name="getTransitionName(currentRoute)" mode="out-in" :css="true">
                    <div :key="getRouteKey(currentRoute.path)">
                        <component :is="Component" v-if="Component" :key="getRouteKey(currentRoute.path)" />
                        <Loading v-else :show="true" :fixed="true" />
                    </div>
                </Transition>
            </keep-alive>
        </router-view>
    </Suspense>
</template>

<style scoped>
    .route-return {
        position: absolute;
        z-index: 1;
        padding: 15px;
        top: 25px;
        font-weight: 700;
    }
</style>

<style>
    /* Route transition styles */
    .route-enter-active,
    .route-leave-active {
        transition: all 150ms ease;
    }

    .route-enter-from,
    .route-leave-to {
        opacity: 0;
    }
</style>
